import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { Card, HeaderStyle, useBreakpoint } from '../../../../ui/components';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { PAGES, trackPage, LoggerService } from '../../../../ui/utils';
import CoachService from '../../../../sdk/services/coachService';
import AttachmentService from '../../../../sdk/services/attachmentService';
import { AppTokenModel, DynamicObjectInterface } from '../../../../sdk/type';
import { useAPICall } from '../../../../hooks';
import TableWithSearchFilterTemplate from '../../../../ui/components/TableWithSearchFilterTemplate/TableWithSearchFilterTemplate';
import { getCoachListConfig } from './coachListConfig';
import styles from './index.module.scss';

interface CoachData {
  name: string;
  coachId: string;
  enrollmentDate: string;
  endDate: string;
  totalNewAbo: number;
  totalPoint: number;
  campaignId: string;
  campaignName: string;
  recId: string;
  status: string;
  loginId: number;
  aboId: number;
  handleRedirect: () => void;
  profileImageUrl?: string;
}

interface CoachProps {
  decodedData: AppTokenModel | null;
  classes?: string;
}

export const Coach = (props: CoachProps) => {
  const apiCall = useAPICall();
  const location = useLocation();
  const navigate = useNavigate();
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm'].indexOf(viewPortType) === -1;
  const paths = location.pathname.split('/');
  const campaignsIdx = paths.indexOf('campaigns');
  let campaignId = '';
  if (campaignsIdx !== -1 && campaignsIdx < paths.length - 1) {
    campaignId = paths[campaignsIdx + 1];
  }

  const [data, setData] = useState<CoachData[]>([]);
  const [searchKey, setSearchKey] = useState('');

  useEffect(() => {
    fetchCoachList();

    const timeout = setTimeout(() => {
      trackPage(PAGES.coachList);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, [location]);

  // const fetchCoachList = async () => {
  //   if (!props.decodedData) return;
  //   let res = await apiCall(CoachService.getCoachByCampaignId, campaignId, props?.decodedData?.loginId);
  //   if (res.data.data) {
  //     const updatedData = res.data.data?.map((d: DynamicObjectInterface) => {
  //       d.totalNewAbo = d.totalPersonalSponsor + d.totalGroupSponsor;
  //       d.totalPoint = d.totalPoint + d.CumulativeAdjustedPoint;
  //       d.handleRedirect = () => navigate(`/campaigns/${d.campaignId}/coach/${d.recId}`);
  //       return d as CoachData;
  //     });
  //     setData(updatedData);
  //     updatedData?.forEach((coach: CoachData) => {
  //       fetchProfileImage(coach.loginId);
  //     });
  //   }
  // };

  // const fetchProfileImage = async (userId: number) => {
  //   try {
  //     const response = await AttachmentService.downloadProfileImage(userId ?? '');
  //     if (response.data && response.data.data && response.data.data.image) {
  //       setData(prevState =>
  //         prevState.map(coach =>
  //           coach.loginId === userId ? { ...coach, profileImageUrl: response.data.data.image } : coach,
  //         ),
  //       );
  //     }
  //   } catch (error) {
  //     LoggerService.log('Error downloading image', error);
  //   }
  // };

  const fetchCoachList = async () => {
    if (!props.decodedData) return;

    try {
      let res = await apiCall(CoachService.getCoachByCampaignId, campaignId, props?.decodedData?.loginId);
      if (res.data.data) {
        const updatedData = await Promise.all(
          res.data.data.map(async (d: DynamicObjectInterface) => {
            d.totalNewAbo = d.totalPersonalSponsor + d.totalGroupSponsor;
            d.totalPoint = d.totalPoint + d.CumulativeAdjustedPoint;
            d.handleRedirect = () => navigate(`/campaigns/${d.campaignId}/coach/${d.recId}`);

            // Fetch profile image
            try {
              const response = await AttachmentService.downloadProfileImage(d.loginId ?? '');
              if (response.data && response.data.data && response.data.data.image) {
                d.profileImageUrl = response.data.data.image;
              }
            } catch (error) {
              LoggerService.log('Error downloading image', error);
              d.profileImageUrl = 'defaultImageUrl'; // Set a default image if there's an error
            }

            return d as CoachData;
          }),
        );

        setData(updatedData);
      }
    } catch (error) {
      LoggerService.log('Error fetching coach list', error);
    }
  };

  const handleSearch = (text: string) => {
    setSearchKey(text);
  };

  const filteredData = data.filter(
    coach =>
      coach.aboId.toString().toLowerCase().includes(searchKey.toLowerCase()) ||
      coach.name.toLowerCase().includes(searchKey.toLowerCase()) ||
      coach.coachId.toLowerCase().includes(searchKey.toLowerCase()),
  );

  return (
    <div className={`${styles['campaign-coach']} ${props.classes ? props.classes : ''}`}>
      <h3 className={styles['campaign-coach-title']}>Coaches</h3>
      {isDesktopView ? (
        <Card title={`Campaign: ${data?.[0]?.campaignName}`} headerStyle={HeaderStyle.HEADER_5}>
          {data && data.length > 0 ? (
            <div>
              <TableWithSearchFilterTemplate
                rowKey="recId"
                handleSearch={handleSearch}
                searchKey={searchKey}
                handleReset={() => handleSearch('')}
                dataSource={filteredData}
                getColumns={getCoachListConfig}
                pagination={false}
                placeholder="Search by Coach Name, Coach ID or ABO ID"
                handleResetFilters={() => handleSearch('')}
              />
            </div>
          ) : (
            <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} className={styles['campaign-coach-opt-card']} />
          )}
        </Card>
      ) : (
        <>
          <h4>{`Campaign: ${data?.[0]?.campaignName}`}</h4>
          {data && data.length > 0 ? (
            <div>
              <TableWithSearchFilterTemplate
                rowKey="recId"
                handleSearch={handleSearch}
                searchKey={searchKey}
                handleReset={() => handleSearch('')}
                dataSource={filteredData}
                getColumns={getCoachListConfig}
                pagination={false}
                placeholder="Search by Team Leader Name, Team Leader ID or ABO ID"
                handleResetFilters={() => handleSearch('')}
              />
            </div>
          ) : (
            <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} className={styles['campaign-coach-opt-card']} />
          )}
        </>
      )}
    </div>
  );
};
